import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import PasteIcon from 'assets/icons/PasteIcon';
import useThemes from 'hooks/useThemes';
import useUserDetail from 'hooks/useUserDetail';
import React, { useEffect, useState } from 'react';
import { FaCheck, FaEye } from 'react-icons/fa';
import { selectCurrentUser } from 'redux/userSlice';
import { formatDate } from 'utils/dateUtils';
import { formatAmountV2 } from 'utils/formatAmount';
import UserReferralModal from './Modals/UserReferralModal';
import ReferralCodeModal from './Modals/ReferralCodeModal';
import { FaPencil } from 'react-icons/fa6';
import { selectCurrentWallet } from '../homeSlice';
import walletApi from 'api/walletApi';

interface CashierStatusProps {}

const CashierStatus: React.FunctionComponent<CashierStatusProps> = ({}) => {
  const { currentTheme } = useThemes();
  const t = useAppTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const { isUserLoggedIn } = useUserDetail();
  const [isCopy, setIsCopy] = useState<boolean>(false);
  const [isOpenReferralCodeModal, setIsOpenReferralCodeModal] = useState<boolean>(false);
  const [isShowReferralModal, setIsShowReferralModal] = useState<boolean>(false);
  const [miniBalance, setMiniBalance] = useState<string>('');
  const currentWallet = useAppSelector(selectCurrentWallet);

  const referralCode =
    currentUser?.kyc?.status === 'APPROVED' ? currentUser?.referralCodeDto?.referralCode : '';

  const handleCopyToClipboard = async () => {
    navigator.clipboard.writeText(referralCode ? referralCode : '');
    setIsCopy(true);
    setTimeout(() => {
      setIsCopy(false);
    }, 3000);
  };

  const getMiniWallet = async () => {
    const { ok, body } = await walletApi.getMiniWallets();
    if (ok && body) {
      setMiniBalance(body.balance);
    }
  };

  let idInterval: any = -1;

  useEffect(() => {
    idInterval = setInterval(() => {
      if (isUserLoggedIn) getMiniWallet();
    }, 5000);
    return () => {
      clearInterval(idInterval);
    };
  }, [isUserLoggedIn, currentUser?.id]);

  const promotionTbetExpiredTooltip = (walletReceive: string) =>
    currentUser?.tbetWallet && (
      <div>
        {currentUser?.tbetWallet.promotionBalanceExpiresIn24hrs
          .filter((promotion) => promotion.walletReceive === walletReceive)
          .map(
            (balance) =>
              balance.point > 0 && (
                <>
                  <span className="row-infor__right span text-white" style={{ fontSize: '10px' }}>
                    {`${formatAmountV2(balance.point || 0, 0)} ${t(
                      'userProfile.rowInfo.promotionBalanceExpired',
                    )} ${formatDate(balance.expiredDate)}`}
                  </span>
                  <br />
                </>
              ),
          )}
      </div>
    );

  const handleReferralCodeClick = () => {
    setIsOpenReferralCodeModal((prev) => !prev);
  };

  return (
    <div className="cashier-status primary-text-color">
      <div className="cashier-status__promotion-container">
        <span className="font-bold ">{`${t('userProfile.rowInfo.promotionBalance')} :`}</span>
        <div className="cashier-status__item">
          <span className="font-bold left-col">- Bet:</span>
          <div className="flex-row items-center row-infor__right gap-2 right-col">
            <span className="span">${formatAmountV2(currentWallet?.promotionBalance || 0, 2)}</span>
            {currentUser?.tbetWallet &&
              currentUser.tbetWallet.promotionBalanceExpiresIn24hrs &&
              currentUser.tbetWallet.promotionBalanceExpiresIn24hrs.filter(
                (promotion) => promotion.walletReceive === 'BET' && promotion.point > 0,
              ).length > 0 && (
                <Tooltip placement={'bottomRight'} title={() => promotionTbetExpiredTooltip('BET')}>
                  <Button
                    type="dashed"
                    shape="circle"
                    icon={<InfoCircleOutlined className="tooltip-icon" />}
                    className="tooltip-button"
                  />
                </Tooltip>
              )}
          </div>
        </div>
        <div className="cashier-status__item">
          <span className="font-bold left-col">- Casino:</span>
          <div className="flex-row items-center row-infor__right gap-2 right-col">
            <span className="span">
              ${formatAmountV2(currentWallet?.casinoPromotionBalance || 0, 2)}
            </span>
            {currentUser?.tbetWallet &&
              currentUser.tbetWallet.promotionBalanceExpiresIn24hrs &&
              currentUser.tbetWallet.promotionBalanceExpiresIn24hrs.filter(
                (promotion) => promotion.walletReceive === 'CASINO' && promotion.point > 0,
              ).length > 0 && (
                <Tooltip
                  placement={'bottomRight'}
                  title={() => promotionTbetExpiredTooltip('CASINO')}
                >
                  <Button
                    type="dashed"
                    shape="circle"
                    icon={<InfoCircleOutlined className="tooltip-icon" />}
                    className="tooltip-button"
                  />
                </Tooltip>
              )}
          </div>
        </div>
        <div className="cashier-status__item">
          <span className="font-bold left-col">- Poker:</span>
          <div className="flex-row items-center row-infor__right gap-2 right-col">
            <span className="span">
              ${formatAmountV2(currentWallet?.pokerPromotionBalance || 0, 2)}
            </span>
            {currentUser?.tbetWallet &&
              currentUser.tbetWallet.promotionBalanceExpiresIn24hrs &&
              currentUser.tbetWallet.promotionBalanceExpiresIn24hrs.filter(
                (promotion) => promotion.walletReceive === 'POKER' && promotion.point > 0,
              ).length > 0 && (
                <Tooltip
                  placement={'bottomRight'}
                  title={() => promotionTbetExpiredTooltip('POKER')}
                >
                  <Button
                    type="dashed"
                    shape="circle"
                    icon={<InfoCircleOutlined className="tooltip-icon" />}
                    className="tooltip-button"
                  />
                </Tooltip>
              )}
          </div>
        </div>
        <div className="cashier-status__item">
          <span className="font-bold left-col">- Minigame:</span>
          <div className="flex-row items-center row-infor__right gap-2 right-col">
            <span className="span">${formatAmountV2(miniBalance || 0, 2)}</span>
          </div>
        </div>
      </div>
      <div className="cashier-status__item">
        <span className="font-bold left-col">{`${t('balance')}:`}</span>
        <div className="flex-row items-center row-infor__right gap-2 right-col">
          <span className="span">${formatAmountV2(currentWallet?.balance || 0, 2)}</span>
        </div>
      </div>
      <div className="cashier-status__item row-wrap">
        <span className="font-bold left-col ">{`${t('userProfile.rowInfo.referralCode')}:`}</span>
        <div className="row-infor__right span right-col">
          <div className="w-full flex justify-center items-center gap-3 referral-container">
            <div className="relative w-full referral-input-container">
              <input
                className="input-modal w-full referral-input"
                type="text"
                name="promotion-code"
                id="promotion-code"
                disabled
                value={referralCode}
              />
              {isCopy ? (
                <FaCheck className="icon-input primary-text-color" />
              ) : (
                <PasteIcon
                  isDarkmode={currentTheme === 'dark'}
                  className="icon-input"
                  onClick={handleCopyToClipboard}
                />
              )}
            </div>
            <Tooltip
              placement={'bottom'}
              title={t('userProfile.rowInfo.referralList')}
              color="#636363"
              mouseLeaveDelay={0}
              showArrow={false}
              zIndex={isShowReferralModal ? 99 : 10000}
            >
              <div className="referral-detail-btn" onClick={() => setIsShowReferralModal(true)}>
                <FaEye />
              </div>
            </Tooltip>
            <Tooltip
              placement={'bottom'}
              title={t('userProfile.rowInfo.referralEdit')}
              color="#636363"
              mouseLeaveDelay={0}
              showArrow={false}
              zIndex={isOpenReferralCodeModal ? 99 : 10000}
            >
              <div className="referral-detail-btn" onClick={handleReferralCodeClick}>
                <FaPencil />
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
      <UserReferralModal
        visible={isShowReferralModal}
        closeModal={() => setIsShowReferralModal(false)}
      />
      {referralCode && (
        <ReferralCodeModal
          title={t('accountSubMenu.referralCode')}
          visible={isOpenReferralCodeModal}
          closeModal={handleReferralCodeClick}
          className="common-modal deposit-modal"
          referralCode={referralCode}
        />
      )}
    </div>
  );
};

export default CashierStatus;
